import React, { useState } from 'react';
import { MARKET_PRICES } from '../constants/pricing';

const SwapModal = ({ isOpen, onClose, defaultFromToken = null, defaultToToken = 'nICP', balances = {} }) => {
  const [fromToken, setFromToken] = useState(defaultFromToken);
  const [toToken, setToToken] = useState(defaultToToken);
  const [fromAmount, setFromAmount] = useState('');
  const [showTokenSelector, setShowTokenSelector] = useState(false);
  const [selectorType, setSelectorType] = useState('from'); // 'from' or 'to'
  const [maxSlippage] = useState('1.5');
  const [searchQuery, setSearchQuery] = useState('');

  // Token data with real user balances from data model
  const tokens = [
    { symbol: 'ICP', name: 'Internet Computer', balance: balances.ICP || 0, icon: '∞', network: 'Internet Computer' },
    { symbol: 'ckETH', name: 'Chain Key Ethereum', balance: balances.ckETH || 0, icon: '⟠', network: 'Internet Computer' },
    { symbol: 'EXE', name: 'Windoge98', balance: balances.EXE || 0, icon: '🐕', network: 'Internet Computer' },
    { symbol: 'PANDA', name: 'ICPanda', balance: balances.PANDA || 0, icon: '🐼', network: 'Internet Computer' },
    { symbol: 'VCHF', name: 'VNX Franc', balance: balances.VCHF || 0, icon: '🏦', network: 'Internet Computer' },
    { symbol: 'CHAT', name: 'CHAT', balance: balances.CHAT || 0, icon: '💬', network: 'Internet Computer' },
    { symbol: 'nICP', name: 'Neuron ICP', balance: balances.nICP || 0, icon: '💧', network: 'Internet Computer' },
  ];

  if (!isOpen) return null;

  const getTokenPrice = (symbol) => {
    return MARKET_PRICES[symbol] || 0;
  };

  const getTokenUsdValue = (symbol, amount) => {
    const price = getTokenPrice(symbol);
    return (amount * price).toFixed(2);
  };

  const calculateReceiveAmount = () => {
    if (!fromToken || !toToken || !fromAmount) return '0';
    
    const fromPrice = getTokenPrice(fromToken);
    const toPrice = getTokenPrice(toToken);
    
    if (fromPrice && toPrice) {
      const usdValue = parseFloat(fromAmount) * fromPrice;
      const receiveAmount = usdValue / toPrice;
      return receiveAmount.toFixed(6);
    }
    return '0';
  };

  const getTokenBalance = (symbol) => {
    const token = tokens.find(t => t.symbol === symbol);
    return token ? token.balance : 0;
  };

  const getTokenData = (symbol) => {
    return tokens.find(t => t.symbol === symbol);
  };

  const filteredTokens = tokens.filter(token => 
    token.symbol.toLowerCase().includes(searchQuery.toLowerCase()) ||
    token.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleTokenSelect = (token) => {
    if (selectorType === 'from') {
      setFromToken(token.symbol);
    } else {
      setToToken(token.symbol);
    }
    setShowTokenSelector(false);
    setSearchQuery('');
  };

  const handleSwapDirection = () => {
    const tempToken = fromToken;
    setFromToken(toToken);
    setToToken(tempToken);
    setFromAmount('');
  };

  const handleMaxAmount = () => {
    if (fromToken) {
      const balance = getTokenBalance(fromToken);
      setFromAmount(balance.toString());
    }
  };

  const receiveAmount = calculateReceiveAmount();
  const receiveUsdValue = toToken ? getTokenUsdValue(toToken, receiveAmount) : '0.00';

  if (showTokenSelector) {
    return (
      <div className="modal-overlay" onClick={() => setShowTokenSelector(false)}>
        <div className="swap-modal token-selector-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2 className="modal-title">Select token to {selectorType === 'from' ? 'pay' : 'receive'}</h2>
            <button className="modal-close" onClick={() => setShowTokenSelector(false)}>×</button>
          </div>

          <div className="modal-body">
            <div className="token-search-container">
              <div className="search-input-wrapper">
                <input
                  type="text"
                  placeholder="Search for the token"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="token-search-input"
                />
                <span className="search-icon">🔍</span>
              </div>
              <button className="network-filter-button">
                <span className="network-icon">∞</span>
                Internet Computer
              </button>
            </div>

            <div className="token-list">
              {filteredTokens.map((token) => (
                <div
                  key={token.symbol}
                  className="token-list-item"
                  onClick={() => handleTokenSelect(token)}
                >
                  <div className="token-info">
                    <div className="token-icon">{token.icon}</div>
                    <div className="token-details">
                      <div className="token-name-row">
                        <span className="token-symbol">{token.symbol}</span>
                        <span className="token-full-name">• {token.name}</span>
                      </div>
                      <div className="token-network">
                        <span className="network-icon">∞</span>
                        {token.network}
                      </div>
                    </div>
                  </div>
                  <div className="token-balance-info">
                    <div className="token-balance">{token.balance.toFixed(token.balance < 1 ? 6 : 4)}</div>
                    <div className="token-usd-value">CHF {getTokenUsdValue(token.symbol, token.balance)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="modal-actions">
            <button 
              className="action-button secondary full-width"
              onClick={() => setShowTokenSelector(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="swap-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Swap</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {/* You pay section */}
          <div className="swap-section">
            <label className="swap-label">You pay</label>
            <div className="swap-input-container">
              <input
                type="number"
                value={fromAmount}
                onChange={(e) => setFromAmount(e.target.value)}
                placeholder="0"
                className="swap-amount-input"
              />
              <button 
                className="token-selector-button"
                onClick={() => {
                  setSelectorType('from');
                  setShowTokenSelector(true);
                }}
              >
                {fromToken ? (
                  <span className="selected-token">
                    <span className="token-icon">{getTokenData(fromToken)?.icon}</span>
                    {fromToken}
                  </span>
                ) : (
                  <span className="select-token-text">Select token</span>
                )}
                <div className="selector-controls">
                  <span className="add-icon">+</span>
                  <span className="dropdown-arrow">▼</span>
                </div>
              </button>
              {fromToken && fromAmount && (
                <button className="max-button-swap" onClick={handleMaxAmount}>
                  MAX
                </button>
              )}
            </div>
            {fromToken && (
              <div className="token-balance-display">
                Balance: {getTokenBalance(fromToken).toFixed(4)} {fromToken}
              </div>
            )}
          </div>

          {/* Swap direction button */}
          <div className="swap-direction-container">
            <button className="swap-direction-button" onClick={handleSwapDirection}>
              <span className="swap-arrows">⇅</span>
            </button>
          </div>

          {/* You receive section */}
          <div className="swap-section">
            <label className="swap-label">You receive</label>
            <div className="swap-input-container">
              <div className="receive-amount">{receiveAmount}</div>
              <button 
                className="token-selector-button"
                onClick={() => {
                  setSelectorType('to');
                  setShowTokenSelector(true);
                }}
              >
                <span className="selected-token">
                  <span className="token-icon">{getTokenData(toToken)?.icon}</span>
                  {toToken}
                </span>
                <div className="selector-controls">
                  <span className="dropdown-arrow">▼</span>
                </div>
              </button>
            </div>
            <div className="receive-details">
              <span className="receive-usd">↗ CHF {receiveUsdValue}</span>
              <span className="receive-balance">📋 {getTokenBalance(toToken).toFixed(3)} {toToken}</span>
            </div>
          </div>

          {/* Max slippage */}
          <div className="slippage-container">
            <span className="slippage-label">Max slippage</span>
            <div className="slippage-selector">
              <span className="slippage-value">{maxSlippage}%</span>
              <span className="dropdown-arrow">▼</span>
            </div>
          </div>
        </div>

        <div className="modal-actions">
          <button 
            className="action-button secondary"
            onClick={onClose}
          >
            Cancel
          </button>
          <button 
            className="action-button primary"
            disabled={!fromToken || !toToken || !fromAmount}
          >
            Review swap
          </button>
        </div>
      </div>
    </div>
  );
};

export default SwapModal;
